import pluralize from 'pluralize';

const DEFAULT_SEASON_NAME = 'Default';

export const labels = {
	app: {
		name: 'BestREV 2.0',
		support_email: 'service@bwh.com',
		release_date: '2023-01-03',
		support_button: 'Email Support',
	},
	common: {
		save: 'Save',
		yes: 'Yes',
		no: 'No',
		cancel: 'Cancel',
		confirm: 'Confirm',
		priority: 'Priority',
		none: 'None',
		rec_levels: ['low', 'medium', 'high'],
		delete: 'Delete',
	},
	comp_rates: {
		tooltip: {
			stay_date: 'Stay Date:',
			shop_date: 'Shop Date:',
			rate: 'Rate:',
			room_name: 'Room Name:',
			los: 'LOS:',
			best_flex: 'Best Flex?:',
			rate_changes: 'Rate Changes',
			days_ago: (day: number) => `${day} ${pluralize('day', day)} ago:`,
		},
		messages: {
			missing: 'N/A',
			soldout: 'Sold Out',
			noBar: 'No BstFlx',
			no1p: 'No 1P',
			no2p: 'No 2P',
			noBreakfast: 'No Bkfst', // cspell:ignore Bkfst
			noRmType: 'No RmType',
			los2: 'LOS2',
			los3: 'LOS3',
		},
	},
	router_prompt_modal: {
		title: 'Unsaved changes exist !!',
		message:
			'You have unsaved changes. Please continue without saving or cancel to stay on this page.',
		btn_confirm_text: 'Continue',
	},
	rate_upload: {
		tooltip_title: 'Upload Selected Rates',
		header: 'Confirm Rate Upload',
		verify_msg:
			'Please verify the following pending rate changes before confirming the upload. You may need to scroll down to view all rates.',
		confirm_btn: 'Upload',
	},
	add_form: {
		required: 'Required',
		start_date_err: '"Start Date" cannot be in the past',
		end_date_err: '"End date" should be greater than or equal to start date',
		required_day_err: 'At least 1 day is required',
		invalid_day_err:
			'Please select a day that falls within the selected date range',
		season_name: 'Season Name',
		start_date: 'Start Date',
		end_date: 'End Date',
		highlight_myview: 'Highlight in My View',
		priority: 'Priority',
		last_updated_by: 'Last updated by',
		on: 'on',
	},
	daypicker: {
		non_valid_days_tooltip_msg: 'The chosen date range does not include any',
		valid_days_tooltip_msg: 'The chosen date range include all',
	},
	autocomplete: {
		no_property_selected: 'No property selected',
		no_property_found: 'No matching properties found',
		placeholder: 'Please login to search',
		searching: 'Searching...',
	},
	header: {
		logo_text: 'BestREV',
		property_search_placeholder: 'Search Property',
		info_popup: {
			header: 'About',
			message: 'Please write all product questions and comments to',
		},
		user_details: {
			default_initials: 'BWH',
			logout: 'Log Out',
			login: 'Log In',
		},
		tooltip: {
			askRev: 'Ask BestREV',
			about: 'About',
			alerts: 'Sellout Alerts',
			rate_change_logs: 'Rate Change Logs',
			rate_offset_logs: 'Rate Offset Logs',
		},
	},
	nav_bar: {
		home: 'Home',
		my_view: 'My View',
		events: 'Events',
		insights: 'Insights',
		rules: 'Rules',
		settings: 'Settings',
		help: 'Help',
		admin: 'Admin',
		groups: 'Groups',
	},
	ask_best_rev: {
		initial_message: 'Use the Ask BestRev feature to filter and view your data',
		title: 'Ask BestREV',
		btn_submit: 'Submit',
		btn_clear: 'Clear',
		placeholder: 'Ask BestREV anything...',
		loading: 'Processing query...',
	},
	home: {
		salutation: 'Hello',
		recs_message: (upToDate: boolean) =>
			`You have ${upToDate ? 'new' : 'older'} price recommendations`,
		generated_dates_message: (start: string, end: string) =>
			`generated for ${start} through ${end}`,
		last_updated_message: (updatedAt: string) =>
			`as of ${updatedAt} hotel's local time`,
		alert_messages: {
			warning: 'BWH Rate less than Market Rate with forecasted occupancy > 90%',
			insight: '12% of capacity booked in the last 12 hours',
		},
		night: 'Night',
		nights: 'Nights',
		review: 'Review',
		incr: 'Incr',
		decr: 'Decr',
		revenue: 'Revenue',
		months: [
			'JAN',
			'FEB',
			'MAR',
			'APR',
			'MAY',
			'JUN',
			'JUL',
			'AUG',
			'SEP',
			'OCT',
			'NOV',
			'DEC',
		],
		rec_statuses: {
			N: 'No Change',
			P: 'Pending',
			AP: 'Autopilot',
			S: 'Sent',
			R: 'Review',
			E: 'Error',
		},
		tooltip: {
			filter_rec_status: 'Filter by status',
			remove_filter: 'Remove Filter',
			priority_filter: 'Priority (Click to filter)',
			upload: 'Upload Rate',
			reset: 'Reset Changes',
		},
		bw_rate: 'BWH Rate',
		yoy_rate: 'YOY Rate',
		available_rooms: 'Avail Rooms',
		otb: 'OTB',
		rec_rate: 'Rec Rate',
		market_rate: 'Market Rate',
		rate_to_upload: 'Rate to Upload',
		adr: 'ADR',
		inventory_labels: {
			grp_blocked: 'Group Blocked',
			grp_booked: 'Group Booked',
			out_of_order: 'Out-of-Order',
			transient: 'Transient Booked',
			phy_cap: 'Physical Capacity',
		},
		header_names: {
			level: 'Level',
			otb: 'Curr OTB',
			fcst: 'Forecasted\nRooms to be Sold',
			status: 'Curr Status',
			rec_action: 'Rec Action',
		},
		no_data: 'No recent data to display',
	},
	login: {
		greeting: 'Please login to continue',
	},
	rec_review: {
		chart: {
			room: 'Rooms',
			rates: 'Rates',
			total_fcst: 'Total Forecast',
			total_fcst_incl_group: 'Total Fcst (incl Grp)',
			current_otb: 'Current OTB',
			stay_date: 'Stay Date',
			graph: {
				demand: 'Demand vs Rate',
				inventory: 'Inventory',
			},
			return_to_grid: 'Return to Grid View',
		},
		days: {
			day30: '30 days',
			day60: '60 days',
			day90: '90 days',
			day120: '120 days',
			day365: '365 days',
		},
		weeks: {
			all: 'All Days',
			weekends: 'Weekends',
			weekdays: 'Weekdays',
		},
		dashboard: {
			missing_status_alert:
				'Some or all of the stay dates are missing statuses and may not be filtered correctly',
			warning_header: 'Potential Sellout !!',
			override_cell: {
				old_snapshot: 'Recommendations last updated',
			},
			tooltip: {
				adr: 'Average Daily Rate',
				adr_lyf: 'ADR (Last Year Final)',
				adr_lyst: 'ADR (Last Year Same Time)',
				alert: 'Alert',
				available_rooms: 'Available Rooms',
				bw_rate: 'Best Western Rate',
				con_dmd_curr_rate: 'Constrained Demand @ BWH rate',
				con_dmd_rec_rate: 'Constrained Demand @ Rec rate',
				current_revenue: 'Current Revenue',
				date: 'Stay Date',
				derived_rate: 'Derived Rate',
				dow: 'Day of Week',
				event: 'Event',
				favorite: 'Favorite',
				fcst_lvl_rate: 'OTB / Forecast / Status / Action',
				fcst_occ_pct: 'Forecast Occupancy (Includes Group)',
				fcst_occ_pct_transient: 'Forecast Occupancy (Transient Only)',
				fcst_rev: 'Forecast Revenue',
				final_rec_rate: 'Final Rec Rate',
				group_blocked: 'Group Blocked',
				group_booked: 'Group Booked',
				last_current_rate: 'Previous Current Rate',
				market_rate: 'Market Rate',
				market_rate_live: 'Market Rate (Live)',
				market_rate_lyf: 'Market Rate (Last Year Final)',
				market_rate_lyst: 'Market Rate (Last Year Same Time)',
				occ_pct: 'Occupancy (Includes Group)',
				occ_pct_lyf: 'Occupancy (Last Year Final)',
				occ_pct_lyst: 'Occupancy (Last Year Same Time)',
				ooo: 'Out-of-Order',
				otb: 'On the Books (Includes Group)',
				otb_lyf: 'On the Books (Last Year Final)',
				otb_lyst: 'On the Books (Last Year Same Time)',
				physical_capacity: 'Physical Capacity',
				pkp_day_1: '1-Day Pickup',
				pkp_day_1_ly: '1-Day Pickup (Last Year)',
				pkp_day_3: '3-Day Pickup',
				pkp_day_3_ly: '3-Day Pickup (Last Year)',
				pkp_day_7: '7-Day Pickup',
				pkp_day_7_ly: '7-Day Pickup (Last Year)',
				pkp_doa_ly: 'Pick-up day of arrival last year',
				transient_pickup_day_1: '1-Day Transient Pickup',
				transient_pickup_day_3: '3-Day Transient Pickup',
				transient_pickup_day_7: '7-Day Transient Pickup',
				group_pickup_day_1: '1-Day Group Pickup',
				group_pickup_day_3: '3-Day Group Pickup',
				group_pickup_day_7: '7-Day Group Pickup',
				prev_rec_rate: 'Previous Rec Rate',
				priority: 'Priority',
				rate_to_upload: 'Rate to Upload',
				rate_variance: 'Rate Variance (Rec vs Prev)',
				rec_rate: 'Recommended Rate',
				rec_rate_desc:
					"BestREV2.0 uses its subject hotel's current and historical pricing and demand pattern against its competitors when recommending a price change",
				rec_status: 'Recommendation Status',
				revpar: 'RevPAR',
				revpar_lyf: 'RevPAR (Last Year Final)',
				revpar_lyst: 'RevPAR (Last Year Same Time)',
				total_fcst: 'Total Forecast (Includes Group)',
				transient_cap: 'Transient Capacity',
				transient_fcst: 'Transient Forecast',
				transient_fcst_event: 'Forecast Override',
				unc_transient_fcst: 'Unconstrained Transient Forecast',
				unc_dmd_curr_rate: 'Unconstrained Demand @ BWH rate',
				yoy_rate_lyf: 'Yoy Rate (Last Year Final)',
				yoy_rate_lyst: 'Yoy Rate (Last Year Same Time)',
			},
			forecast_info: {
				header: (changeType: '' | 'inc' | 'dec', changeInRecRate: number) =>
					changeType
						? `Recommend ${
								changeType === 'inc' ? 'Increase' : 'Decrease'
							} $${Math.round(Math.abs(changeInRecRate))}`
						: 'Recommend Maintain Rate',
				new_rec_rate: 'New Rec',
				as_of: 'as of',
				prev_rec_rate: 'Prev Rec',
				market_rate_change: 'Change in Market Rate',
				rates: 'Rates',
				current: 'Current Rate',
				recommended: 'Rec Rate',
				pricing_ratio: 'Pricing Ratio',
				pickup_ratio: 'Pickup Ratio',
				incremental_revenue: 'Incremental Revenue',
				pickup: 'Pickup',
				last_hour: 'Last Hour',
				since_12: 'Since 12:00am',
				expected: 'Expected',
				actual: 'Actual',
				reasons: 'Reasons',
				last_hour_pickup: 'Last Hour Pickup',
				pickup_since_12: 'Pickup Since 12:00am',
				expected_pickup: 'Expected Pickup',
				actual_pickup: 'Actual Pickup',
				expected_all_day: 'Expected All Day',
				expected_all_day_pickup: 'Expected All Day Pickup',
				unconstrained: 'unconstrained',
				remaining: 'Remaining',
				reason_codes: {
					1: '3-hour pickup is higher than usual',
					2: '3-hour pickup is typical',
					3: '3-hour pickup is lower than usual',
					4: 'rate relative to market is higher than usual',
					5: 'rate relative to market is typical',
					6: 'rate relative to market is lower than usual',
					7: 'hotel is not expected to sellout',
					8: 'hotel is expected to sellout',
					9: 'rate is limited by maximum rate',
					10: 'rate is limited by minimum rate',
					11: 'rounding rule is being applied',
				},
			},
			price_decision: {
				recommended_action: 'Recommended Action',
				inc: 'Increase Rate',
				dec: 'Decrease Rate',
				nc: 'No change to BW Rate',
				pickup: 'Pickup Ratio',
				price: 'Pricing Ratio',
				eval_demand_pattern: 'Evaluation of Pricing & Demand Pattern',
			},
			more_action: {
				deselect_all: 'Deselect All',
				export: 'Export',
				graph: 'Show Graph',
				more_options: 'More Options',
				refresh: 'Reset/Refresh',
				save: 'Save',
				select_all: 'Select All',
				suppress_warnings: 'Suppress Warnings',
				upload: 'Upload Selected',
				warnings_suppressed: 'Warnings Suppressed',
				add_column_to_metric: 'Add to My Metrics',
				column_added: 'Added to My Metrics',
				remove_column_from_metric: 'Remove from My Metrics',
				reset_columns: {
					header: 'Reset Columns',
					message:
						'Are you sure you want to reset column selections to default position?',
					confirm: 'Yes',
					cancel: 'No',
				},
				create_season: 'Create Season',
				create_event: 'Create Event',
				create_mask: 'Create Mask',
				show_season: 'Show Season',
				do_not_autopilot: 'Do Not Autopilot',
				show_past: 'Show Past 7 Days',
				hide_past: 'Hide Past 7 Days',
			},
			bw_rate: 'BWH Rate',
			market_rate: 'Market Rate',
			rec_rate: 'Rec Rate',
			yoy_rate: 'YOY Rate',
			available_rooms: 'Available Rooms',
			available_cap: 'Available Capacity',
			group_booked: 'Group Booked',
			group_blocked: 'Group Blocked',
			transient_booked: 'Transient Booked',
			overbooking: 'Overbooking',
			out_of_order: 'Out-of-Order',
			total_capacity: 'Total Capacity',
		},
		upload_rates_confirmation: 'Rate upload successfully triggered',
		upload_single_rate_modal: {
			header: 'Rate Upload Confirmation',
			message: 'Are you sure you want to upload the recommended rate?',
		},
		reset_rates_modal: {
			header: 'Warning - Resetting Rates',
			message:
				'This will cause your rate(s) to reset to the original rate recommendation. Are you sure you want to continue?',
		},
		rate_shop_modal: {
			header: 'Customize Rate Shop Details',
			rate_type: 'Rate Type',
			los: 'LOS',
			channel: 'Channel',
			room_type: 'Room Type',
			change_days: 'Change Days',
		},
		search: {
			search_names: {
				default: 'All days - Next 120 days',
				alert: 'Alerts',
				rec_price_change: 'Rec Price Change - Next 90 days',
			},
			search_name_placeholder: 'Search Name',
			save_search_msg: 'Search saved successfully',
			delete_search_msg_success: 'Search deleted successfully',
			delete_search_msg_error: 'Search failed to delete',
			copy: 'Copy of ',
			save: 'Save',
			continue: 'Continue',
			confirmation: 'Confirmation',
			yes: 'Yes',
			no: 'No',
			delete_search_msg: 'Would you like to delete your search?',
			discard_search_msg: 'Would you like to discard your search?',
			btn_save_as: 'Save as',
			btn_save: 'Save',
			btn_reset: 'Reset',
			btn_search: 'Search',
			btn_create_new_search: 'Create New Search',
			btn_add_search_criteria: 'Add Search Criteria',
			label_days: 'days',
			search_rhs_value: 'Value',
			form_error: {
				required_search_name: 'Enter Search name',
				required_day_err: 'At least one day is required',
				required_status_err: 'Select at least one status',
				search_name_copy_err:
					'Please use different search name to copy this search',
				search_name_exist_err: 'Search name exists already; please try another',
				max_search_name_err: 'Search name should not exceed 50 characters',
			},
			tooltip: {
				create_search: 'Create New Search',
				reset: 'Reset',
				delete_search: 'Delete Search',
				manage_search: 'Manage Search',
			},
		},
	},
	rules: {
		seasons: 'Seasons',
		competitors: 'Competitors',
		rates: 'Rates',
		autopilot: 'Autopilot',
		default_season_name: DEFAULT_SEASON_NAME,
		modify_confirmation_msg: 'Season successfully updated!',
		modify_error_msg: 'Season update failed',
		create_confirmation_msg: 'Season created successfully',
		create_error_msg: 'Season creation failed',
		copy: 'Copy of ',
		continue: 'Continue',
		finish: 'Finish',
		back: 'Back',
		confirmation: 'Confirmation',
		ok: 'Ok',
		overlap_confirmation_message:
			'This season conflicts with the below season(s). Please adjust the priorities here to resolve the conflict.',
		confirmation_message:
			'Would you like to discard your changes and create a new season?',
		current: 'Current',
		priority: 'Priority',
		new: 'New',
		competitor_table: {
			name: 'Name',
			address: 'Address',
			city: 'City',
			state: 'State',
			zip: 'Zip',
			country: 'Country',
			rating: 'Rating',
			review: 'No. of Reviews',
			weighting_percentage: 'Weighting %',
			total: 'Total',
			save_success_msg: 'Changes have been successfully saved',
			save_failed_err: 'Failed to save changes',
			season_not_selected_err: 'Please select a season to save competitor set',
			no_competitors_error: 'Seasons must have at least one competitor',
			total_weighting_error: 'Weightings should total 100%',
		},
		no_prop_select_err: 'Please select a property to create a season',
		already_exist_err:
			'This season already exists.  Please provide a new season name and/or start date.',
		pricing_strategy: {
			info_tooltip:
				'The goal of having conservative, neutral and aggressive pricing strategies is to provide the ability to choose how dynamic the pricing is.',
			info_tooltip_hint:
				'Hover over each option to see how it will impact the frequency and magnitude of rate changes.',
			conservative:
				'Makes pricing more static. BestRev will accept fewer recommendations to increase or decrease rates, and will do so by a smaller magnitude. Expect fewer and smaller rate changes.',
			neutral: 'Corresponds to the current pricing with no change.',
			aggressive:
				'Makes pricing more dynamic. BestRev will accept more recommendations to increase or decrease rates, and will do so by a larger magnitude. Expect more and larger rate changes.',
		},
		autopilot_tab: {
			threshold_high_err: 'Max value should be greater than Min value.',
			positive_only: 'Positive only',
			min: 'Min',
			max: 'Max',
			high: 'High',
			med: 'Med',
			low: 'Low',
			info: {
				header_1: 'How can Autopilot help you?',
				paragraph_1:
					'It can automatically send price recommendations to the Reservation system based on your threshold definition',
				header_2: 'Increased RevPAR',
				paragraph_2:
					'Hotels that autopilot see an increased RevPAR growth year-over-year compared to non-autopilot hotels',
			},
			save_success_msg: 'Autopilot settings saved successfully',
		},
	},
	events: {
		event_name: 'Event Name',
		event_type: 'Event Type',
		name: 'Name',
		start_date: 'Start Date',
		end_date: 'End Date',
		occupancy_impact: 'Anticipated Occ % Impact',
		mul_copy_err: 'Only one event can be copied at a time.',
		noSelect_copy_err: 'Please select a event to copy.',
		modify_confirmation_msg: 'Event successfully updated!',
		modify_error_msg: 'Event update failed',
		create_confirmation_msg: 'Event created successfully',
		create_error_msg: 'Event creation failed',
		active: 'Active',
		past: 'Past',
		new_event: 'New Event',
		ascending: 'Asc',
		descending: 'Desc',
		noSelect_delete_err: 'Please select at least one event to delete.',
		past_event_edit_err: 'Past event cannot be edited.',
		delete_single_success_msg: 'event has been deleted!',
		delete_multiple_success_msg: 'events have been deleted!',
		delete_event_record_success_msg: 'event record has been deleted!',
		confirmation_message:
			'Are you sure you want to delete the below events? \n',
		modal_header_text: 'Confirmation',
		add_dates_text: 'Add Dates',
		validation: {
			required: 'Required',
			start_date_err: '"Start Date" cannot be in the past',
			end_date_err: '"End date" should be greater than or equal to start date',
		},
		tooltip: {
			add_event_record: 'Add event occurrence',
			remove_event_record: 'Remove event occurrence',
		},
	},
	season_list: {
		active: 'Active',
		past: 'Past',
		mul_copy_err: 'Only one season can be copied at a time.',
		noSelect_delete_err: 'Please select at least one season to delete.',
		past_season_edit_err: 'Past season cannot be edited.',
		delete_single_success_msg: 'season has been deleted!',
		delete_multiple_success_msg: 'seasons have been deleted!',
		modal_header_text: 'Confirmation',
		confirmation_message:
			'Are you sure you want to delete the below seasons? \n',
		name: 'Name',
		start_date: 'Start Date',
		ascending: 'Asc',
		descending: 'Desc',
		new_season: 'New Season',
	},
	settings: {
		rounding_rules: {
			show_rules: 'Show Rounding Rules',
			column_headers: {
				rule_id: 'Rule ID',
				price_bound: 'Price Bound',
				description: 'Description',
				format: 'Rounding Format',
				format_above: 'Format Above Bound',
				method: 'Rounding Method',
				use_bound: 'Use Price Bound',
			},
		},
	},
	alerts: {
		propertyAccess:
			'Property access denied. Please select a different property',
		insufficientRole: 'Insufficient role to perform this action',
	},
	chat: {
		title: 'RevBot',
		minimize: 'Minimize Chat',
		close: 'Close Chat',
		send: 'Send',
		placeholder: 'Type your message here...',
		error: 'Sorry, I am unable to process your request at the moment.',
	},
	group_pricing: {
		profit_margin_tooltip:
			"This is the minimum profit your hotel wants to make from a group. Set the slider to the profit percentage your hotel typically expects, for example 12%. The tool will only recommend accepting a group if the group's profit is higher than or equal to your chosen profit margin and better than what you expect to earn from your forecasted transient or on-the-books business.",
		net_benefit_error: 'Error fetching net benefit data',
		room_availability_error: 'Error fetching room availability data',
		save_group_success: 'Group saved successfully',
		save_group_error: 'Error saving group',
		update_group_success: 'Group updated successfully',
		update_group_error: 'Error updating group',
		delete_group_success: 'Group deleted successfully',
		delete_group_error: 'Error deleting group',
	},
	admin: {
		user_table: {
			header: {
				name: 'Name',
				email: 'Email',
				role: 'Role',
				last_login: 'Last Login',
				super_admin: 'Super Admin',
				actions: 'Actions',
				properties: 'Assigned Properties',
				okta_id: 'Okta ID',
				login_id: 'Login ID',
			},
			tooltip: {
				edit_user: 'Edit User',
				view_logs: 'View Logs',
			},
			modal: {
				edit: {
					header: 'Edit User',
					description: 'Update user information and access permissions.',
				},
				logs: {
					header: 'User Logs',
				},
			},
			btn: {
				cancel: 'Cancel',
				save: 'Save',
				next: 'Next',
				previous: 'Previous',
			},
			text: {
				no_properties_assigned: 'No properties assigned',
				properties: 'Properties',
				no_users_found: 'No users found',
				all: 'All',
			},
			placeholder: {
				search_users: 'Search Users...',
				filter_by_role: 'Filter by Role',
			},
		},
		autopilot_switch: {
			header: 'App Autopilot Switch',
			description:
				'Controls Autopilot functionality for the entire application.',
			autopilot_enabled: 'Autopilot is currently ENABLED',
			autopilot_disabled: 'Autopilot is currently DISABLED',
			autopilot_enabled_info:
				'All Autopilot features are active across the application.',
			warning:
				'Warning: Autopilot features are disabled system-wide. This may affect critical functionality.',
		},
		support_message_form: {
			label: 'Support Message',
			btn: {
				save: 'Save',
				clear_message: 'Clear Message',
				submit: 'Submit Article',
				revBot: 'Add to RevBot KB',
			},
			switch: {
				autopilot_for_property: 'Autopilot for Property: ',
				bestrev_for_property: 'BestREV for Property: ',
			},
		},
	},
};
